import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { getTutors, getTutor, loadMoreTutors } from './api';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';

interface ITutorRate {
  amount: number;
  currency: string;
}

export interface ITutorBadge {
  text: string;
  color: string;
}

export interface ITutor extends IUser {
  rate?: ITutorRate;
  badge?: ITutorBadge;
  tag?: string;
}

export interface ITutorsState {
  status: 'idle' | 'loading' | 'rejected';
  tutorData: ITutor | undefined;
  tutorsList?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITutor>;
    studentCurrency: string;
    maxRateAmount?: number;
  };
}

const initialState: ITutorsState = {
  status: 'idle',
  tutorData: undefined,
  tutorsList: undefined,
};

export const TutorsSlice = createSlice({
  name: 'tutors',
  initialState,
  reducers: {
    resetTutor: (state) => {
      state.tutorData = undefined;
    },
    resetTutors: (state) => {
      state.tutorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET TUTOR BY ID
      .addCase(getTutor.fulfilled, (state, action) => {
        state.status = 'idle';
        state.tutorData = action.payload;
      })
      // GET TUTORS
      .addCase(getTutors.fulfilled, (state, action) => {
        state.status = 'idle';
        const {
          limit,
          offset,
          totalDocs = 0,
          docs = [],
          studentCurrency,
          maxRateAmount,
        } = action.payload;

        state.tutorsList = {
          limit,
          offset,
          totalDocs,
          list: docs,
          studentCurrency,
          maxRateAmount,
        };
      })
      // LOAD MORE TUTORS
      .addCase(loadMoreTutors.fulfilled, (state, action) => {
        state.status = 'idle';
        const { docs = [] } = action.payload;

        if (state.tutorsList?.list && docs.length) {
          state.tutorsList = {
            ...state.tutorsList,
            list: [...state.tutorsList.list, ...docs],
            maxRateAmount: state.tutorsList.maxRateAmount,
          };
        }
      });

    const commonActions = [getTutor, getTutors, loadMoreTutors] as const;

    builder
      // MATCHER
      .addMatcher(isPending(...commonActions), (state) => {
        state.status = 'loading';
      })
      .addMatcher(isRejected(...commonActions), (state) => {
        state.status = 'rejected';
      });
  },
});

export const getTutorsState = (state: RootState): RootState['tutors'] =>
  state.tutors;

export const { resetTutor, resetTutors } = TutorsSlice.actions;

export default TutorsSlice.reducer;
